import React, { useState, useEffect, useRef, forwardRef, useImperativeHandle } from 'react';
import { Form, Row, Col } from 'react-bootstrap';
import { Button, Select, DatePicker } from 'antd';
import { PlusOutlined, MinusOutlined  } from '@ant-design/icons';
import dayjs from 'dayjs';
import Api from "../services/api";
import {useParams} from "react-router-dom";

const FilterCohost = ({ startDate, endDate, handleChangeDate, id, onRunReport, props, ref  }) => {
    const [dimensionFilters, setDimensionFilters] = useState([
        { key: '', operator: 'equals', value: [], options: [] },
    ]);

    const [metricFilters, setMetricFilters] = useState([
        { key: '', metric_day: '' },
    ]);

    const containerRef = useRef(null);

    const btnRefs = useRef({});
    const [selectedKeys, setSelectedKeys] = useState(['date']);
    const selectedKeysRef = useRef(['date']);
    const { code, os } = useParams();


    useEffect(() => {
        const handleClickOutside = (event) => {
            if (containerRef.current && !containerRef.current.contains(event.target)) {
                const allPopups = document.querySelectorAll('[data-popup-id]');
                allPopups.forEach(popup => {
                    if (popup.id !== id) {
                        popup.classList.remove('show');
                    }
                });
            }
        };

        document.addEventListener('click', handleClickOutside);
        return () => {
            document.removeEventListener('click', handleClickOutside);
        };
    }, [id]);

    const handleRunReport = () => {
        const priorityKeys = ['media_source', 'campaign_id', 'country'];
        const filterDimensions = priorityKeys.filter((k) => {
            const f = dimensionFilters.find((d) => d.key === k && Array.isArray(d.value) && d.value.length > 0);
            return !!f;
        });

        const currentSelected = selectedKeysRef.current || ['date'];
        const finalSelected = Array.from(new Set([...currentSelected, ...filterDimensions]));
        
        selectedKeysRef.current = finalSelected;
        setSelectedKeys(finalSelected);

        const filterData = {
            startDate,
            endDate,
            dimensionFilters,
            metricFilters,
            selectedDimensions: finalSelected,
        };
        if (onRunReport) onRunReport(filterData);
    };

    const fetchOptionsByDimension = async (dimensionKey, index) => {
        try {
            // Format dates từ Date Range
            const fromDate = startDate ? dayjs(startDate).format('YYYY-MM-DD') : '';
            const toDate = endDate ? dayjs(endDate).format('YYYY-MM-DD') : '';
            
            // Build API params
            const params = {
                dimension: dimensionKey,
                code: code,
                os: os
            };
            
            // Thêm from và to nếu có
            if (fromDate) {
                params.from = fromDate;
            }
            if (toDate) {
                params.to = toDate;
            }
            
            const response = await Api.get(`/api/app/dimension_filter`, { params });
            const options = Array.isArray(response.data.data) ? response.data.data : [];
            const updated = [...dimensionFilters];
            updated[index].options = options;
            setDimensionFilters(updated);
        } catch (error) {
            console.error("Failed to fetch options for dimension:", dimensionKey);
            const updated = [...dimensionFilters];
            updated[index].options = [];
            setDimensionFilters(updated);
        }
    };

    const handleDimensionChange = (index, field, value) => {
        const updated = [...dimensionFilters];
        updated[index][field] = value;
        if (field === 'key') {
            updated[index].value = [];
            updated[index].options = [];
            fetchOptionsByDimension(value, index);
        }
        setDimensionFilters(updated);
    };

    const handleMetricChange = (index, field, value) => {
        const updated = [...metricFilters];
        updated[index][field] = value;
        setMetricFilters(updated);
        
        // Khi đổi metric, điều chỉnh dimensions theo metric được chọn
        if (field === 'key') {
            const allowedMetrics = ['retention', 'dau', 'count_session', 'playtime'];
            if (!allowedMetrics.includes(value)) {
                // Nếu metric không phải là retention, dau, count_session, playtime: bỏ app_version
                setSelectedKeys((prev) => {
                    const next = prev.filter((k) => k !== 'app_version');
                    selectedKeysRef.current = next;
                    return next;
                });
            }
        }
    };

    const handleRemoveDimension = (index) => {
        if (dimensionFilters.length > 1) {
            const updated = dimensionFilters.filter((_, i) => i !== index);
            setDimensionFilters(updated);
        }
    };

    const handleAddDimension = () => {
        setDimensionFilters([
            ...dimensionFilters,
            { key: '', operator: 'equals', value: [], options: [] },
        ]);
    };

    const handleMultiSelectChange = (index, selectedOptions) => {
        const updated = [...dimensionFilters];
        updated[index].value = selectedOptions.map(opt => opt.value); 
        setDimensionFilters(updated);
    };

    useEffect(() => {
        selectedKeysRef.current = selectedKeys;
    }, [selectedKeys]);

    // Đồng bộ Filters khi Dimensions thay đổi
    useEffect(() => {
        const activeDimensions = selectedKeys.filter((key) => key !== 'date');
        setDimensionFilters((prevFilters) => prevFilters.map((filter) => {
            if (!filter.key) {
                return filter;
            }
            if (!activeDimensions.includes(filter.key)) {
                return { key: '', operator: 'equals', value: [], options: [] };
            }
            return filter;
        }));
    }, [selectedKeys]);

    // Nhóm các button chỉ có thể chọn một trong số chúng
    const exclusiveGroup = ['campaign_id', 'app_version', 'ad_group', 'ad'];

    const toggleBtnClass = (key) => {
        setSelectedKeys((prev) => {
            const exists = prev.includes(key);
            let next;
            
            // Nếu key thuộc nhóm exclusive
            if (exclusiveGroup.includes(key)) {
                if (exists) {
                    // Nếu đã được chọn, bỏ chọn nó
                    next = prev.filter((k) => k !== key);
                } else {
                    // Nếu chưa được chọn, bỏ chọn tất cả các button khác trong nhóm và chọn button này
                    next = prev.filter((k) => !exclusiveGroup.includes(k));
                    next.push(key);
                }
            } else {
                // Các button khác hoạt động bình thường (toggle)
                next = exists ? prev.filter((k) => k !== key) : [...prev, key];
            }
            
            selectedKeysRef.current = next;
            return next;
        });
        const btn = btnRefs.current[key];
        if (btn && typeof btn.blur === 'function') btn.blur();
    };

    useImperativeHandle(ref, () => ({
        getSelectedKeys: () => selectedKeysRef.current
    }));

    const dimension_array = [
        { key: "media_source", name: "Ad Network" },
        { key: "country", name: "Country" },
        { key: "campaign_id", name: "Campaign" },
        { key: "app_version", name: "App Version" },
        { key: "ad_group", name: "Ad Group" },
        { key: "ad", name: "Ad" },
    ];

    const dimension_btn_array = [
        { key: "date", name: "Day" },
        { key: "media_source", name: "Ad Network" },
        { key: "country", name: "Country" },
        { key: "campaign_id", name: "Campaign" },
        { key: "app_version", name: "App Version", showOnlyForMetrics: ["retention", "dau", "count_session", "playtime"] },
        { key: "ad_group", name: "Ad Group" },
        { key: "ad", name: "Ad" },
    ];

    const metric_array = [
        { key: "dau", name: "DAU" },
        
        
        { key: "count_session", name: "Sessions" },
        { key: "playtime", name: "Playtime" },
        
        { key: "roas", name: "ROAS" },
        { key: "roi", name: "ROI" },
        { key: "retention", name: "Retention" },
        { key: "daily_pu", name: "Daily Paid Users" },
        { key: "paid_rate", name: "Paid Rate" },
        { key: "ltv", name: "LTV" },
        { key: "iap_ltv", name: "IAP LTV" },
        { key: "ad_ltv", name: "Ad LTV" },
        { key: "revenue", name: "Revenue" },
        { key: "ad_revenue", name: "Ad Revenue"},
        { key: "iap_revenue", name: "IAP Revenue" },
        { key: "banner_ipdau", name: "Banner IPDAU" },
        { key: "banner_rpdau", name: "Banner RPDAU" },
        { key: "reward_ipdau", name: "Reward IPDAU" },
        { key: "reward_rpdau", name: "Reward RPDAU" },
        { key: "inter_ipdau", name: "Inter IPDAU" },
        { key: "inter_rpdau", name: "Inter RPDAU" },
        { key: "appopen_ipdau", name: "App Open IPDAU" },
        { key: "appopen_rpdau", name: "App Open RPDAU" },
    ];

    const metric_day_array = [
        { key: "30", name: "30 Days" },
        { key: "45", name: "45 Days" },
        { key: "60", name: "60 Days" },
        { key: "90", name: "90 Days" },
        { key: "120", name: "120 Days" },
        { key: "150", name: "150 Days" },
        { key: "180", name: "180 Days" },
        { key: "210", name: "210 Days" },
        { key: "240", name: "240 Days" },
        { key: "270", name: "270 Days" },
        { key: "300", name: "300 Days" },
        { key: "330", name: "330 Days" },
        { key: "360", name: "360 Days" },
    ];

    return (
        <div className="p-3 filter-cohost" ref={containerRef} data-popup-id={id} id={id}>
            <div className="row" style={{ marginBottom: '15px' }}>
                <div className="col-md-3">
                    <h5>Date Range</h5>
                </div>
                <div className='col-md-9'>
                    <div className="col-md-3" style={{ paddingLeft: '0px', paddingRight: '18px' }}>
                        <DatePicker.RangePicker
                            value={[dayjs(startDate), dayjs(endDate)]}
                            onChange={(dates) => {
                                if (!dates || dates.length !== 2) return;
                                const [start, end] = dates;
                                handleChangeDate(null, { startDate: start, endDate: end });
                            }}
                            allowClear={false}
                            format="YYYY-MM-DD"
                            style={{ width: '100%' }}
                            getPopupContainer={(trigger) => trigger.parentNode}
                            dropdownClassName="drawer-date-dropdown"
                        />
                    </div>
                </div>
            </div>

            <div className="row" style={{ marginBottom: '15px' }}>
                <div className="col-md-3"><h5>Dimensions</h5></div>
                <div className='col-md-9'>
                    <div className="dimensions-buttons">
                        {dimension_btn_array.map((filter) => {
                            // Kiểm tra metric hiện tại
                            const currentMetric = metricFilters[0]?.key || 'retention';
                            // Ẩn button nếu có showOnlyForMetric và metric không khớp
                            if (filter.showOnlyForMetric && currentMetric !== filter.showOnlyForMetric) {
                                return null;
                            }
                            // Ẩn button nếu có showOnlyForMetrics (array) và metric không nằm trong array
                            if (filter.showOnlyForMetrics && !filter.showOnlyForMetrics.includes(currentMetric)) {
                                return null;
                            }
                            
                            const isActive = selectedKeys.includes(filter.key);
                            // Kiểm tra nếu button này thuộc nhóm exclusive và có button khác trong nhóm đã được chọn
                            const isInExclusiveGroup = exclusiveGroup.includes(filter.key);
                            const hasOtherSelected = isInExclusiveGroup && 
                                exclusiveGroup.some(k => k !== filter.key && selectedKeys.includes(k));
                            const isDisabled = hasOtherSelected && !isActive;
                            
                            return (
                                <Button
                                    key={filter.key}
                                    ref={(el) => (btnRefs.current[filter.key] = el)}
                                    type={isActive ? 'primary' : 'default'}
                                    onClick={() => toggleBtnClass(filter.key)}
                                    disabled={isDisabled}
                                >
                                    {filter.name}
                                </Button>
                            );
                        })}
                    </div>
                </div>
            </div>

            <div className="row" style={{ marginBottom: '15px' }}>
                <div className="col-md-3"><h5>Metric</h5></div>
                <div className='col-md-9'>
                    {metricFilters.map((filter, index) => (
                        <Row className="mb-2 filter-line" key={`met-${index}`}>
                            <Col md={3}>
                                <Select
                                    value={filter.key || 'retention'}
                                    className="w-100"
                                    onChange={(value) => handleMetricChange(index, 'key', value)}
                                >
                                    {metric_array.map(dim => (
                                        <Select.Option key={dim.key} value={dim.key}>{dim.name}</Select.Option>
                                    ))}
                                </Select>
                            </Col>

                            <Col md={2}>
                                <Select
                                    value={filter.metric_day || '30'}
                                    className="w-100"
                                    onChange={(value) => handleMetricChange(index, 'metric_day', value)}
                                >
                                    {metric_day_array.map(dim => (
                                        <Select.Option key={dim.key} value={dim.key}>{dim.name}</Select.Option>
                                    ))}
                                </Select>
                            </Col>
                        </Row>
                    ))}
                </div>
            </div>

            <div className="row" style={{ marginBottom: '15px' }}>
                <div className="col-md-3">
                    <h5>Filters</h5>
                </div>
                <div className='col-md-9'>
                    {dimensionFilters.map((filter, index) => {
                        const usedKeys = dimensionFilters.map((f, i) => i !== index && f.key);
                        // Chỉ hiển thị các dimension có button tương ứng đã được active trong Dimensions (ngoại trừ "date")
                        const activeDimensions = selectedKeys.filter(key => key !== 'date');
                        const availableDimensions = dimension_array.filter(
                            dim => (activeDimensions.includes(dim.key) || dim.key === filter.key) && 
                                   (!usedKeys.includes(dim.key) || dim.key === filter.key)
                        );

                        return (
                            <Row className="mb-2 filter-line" key={`dim-${index}`}>
                                <Col md={3}>
                                    <Select
                                        value={filter.key || undefined}
                                        className="w-100"
                                        placeholder="Select a dimension"
                                        onChange={(value) => handleDimensionChange(index, 'key', value)}
                                        allowClear
                                    >
                                        {availableDimensions.map(dim => (
                                            <Select.Option key={dim.key} value={dim.key}>{dim.name}</Select.Option>
                                        ))}
                                    </Select>
                                </Col>

                                <Col md={2}>
                                    <Select
                                        value={filter.operator}
                                        className="w-100"
                                        onChange={(value) => handleDimensionChange(index, 'operator', value)}
                                    >
                                        <Select.Option value="equals">equals</Select.Option>
                                        <Select.Option value="not_equal">not equal</Select.Option>
                                    </Select>
                                </Col>

                                <Col md={3}>
                                    <Select
                                        mode="multiple"
                                        value={filter.value || []}
                                        onChange={(values) => {
                                            const selectedOptions = (filter.options || []).filter(opt => 
                                                values.includes(opt.value || opt.code)
                                            ).map(opt => ({
                                                value: opt.value || opt.code,
                                                label: opt.label || opt.name
                                            }));
                                            handleMultiSelectChange(index, selectedOptions);
                                        }}
                                        placeholder="Select"
                                        className="w-100"
                                        allowClear
                                        showSearch
                                        filterOption={(input, option) => {
                                            const label = option?.label || option?.children || '';
                                            return label.toLowerCase().includes(input.toLowerCase());
                                        }}
                                    >
                                        {(filter.options || []).map(opt => (
                                            <Select.Option 
                                                key={opt.value || opt.code} 
                                                value={opt.value || opt.code}
                                                label={opt.label || opt.name}
                                            >
                                                {opt.label || opt.name}
                                            </Select.Option>
                                        ))}
                                    </Select>
                                </Col>

                                <Col md={1} className="d-flex align-items-center">
                                    {dimensionFilters.length > 1 && (
                                        <Button
                                            size='small'
                                            type="dashed" shape="circle"
                                            danger
                                            icon={<MinusOutlined />}
                                            onClick={() => handleRemoveDimension(index)}
                                            style={{ marginRight: '10px' }}
                                        />
                                    )}
                                    <Button
                                        size='small'
                                        type="dashed" shape="circle"
                                        icon={<PlusOutlined />}
                                        onClick={handleAddDimension}
                                    />
                                </Col>
                            </Row>
                        );
                    })}
                </div>
            </div>


            <div className="row" style={{ marginBottom: '15px' }}>
                <div className="col-md-3">
                    <Button type="primary" onClick={handleRunReport}>Run Report</Button>
                </div>
            </div>
            <style>{`
                /* Desktop: horizontal buttons with 10px spacing, equal width */
                .filter-cohost .dimensions-buttons { display: flex; flex-wrap: wrap; gap: 10px; }
                .filter-cohost .dimensions-buttons .ant-btn { 
                    width: 130px; 
                    min-width: 130px;
                    max-width: 130px;
                    flex-shrink: 0;
                }
                @media (max-width: 768px) {
                  /* Vertical spacing 10px between items in dimension/metric sections */
                  .filter-cohost .filter-line > [class*="col-"] + [class*="col-"] { margin-top: 10px !important; }
                  .filter-cohost .filter-line > .col + .col { margin-top: 10px !important; }
                  /* Dimensions buttons: 2 per row, full width, 10px gap */
                  .filter-cohost .dimensions-buttons { display: grid; grid-template-columns: 1fr 1fr; gap: 10px; }
                  .filter-cohost .dimensions-buttons .ant-btn { width: 100%; }
                }
            `}</style>
        </div>
    );
};

export default FilterCohost;
